/* VERO — the room, in plain JavaScript. A port of the Next site's stage
   (Shelf.tsx, ProductOnShelf.tsx, shelf-context.tsx, RouteFrame.tsx,
   Chrome.tsx, Panel.tsx, CartDrawer.tsx, Stage.tsx), behaviour for
   behaviour, timing for timing. One photograph is the interface; choosing
   a product pushes the camera in on a spring, erases the others and turns
   the copy over. The cart is Shopify's Ajax cart, checkout is Shopify's.
   No framework, no build step.

   Everything the DOM needs is resolved by init() and resolved again
   whenever Shopify re-renders the section (the theme editor and the
   hot-reload client swap section markup without running scripts), so the
   room never depends on a node that has since been replaced. */
(function () {
  'use strict';
  var COMPACT = '(max-width: 1023px) and (orientation: portrait), (max-aspect-ratio: 1249/1000)';
  var REDUCED = '(prefers-reduced-motion: reduce)';
  var COMPACT_REST = { focus: { x: 0.44, y: 0.5 }, scale: 1.3 };
  var PRODUCT_RE = /\/products\/([^\/?#]+)/;
  var SPRING = { stiffness: 88, damping: 26, mass: 1 };
  var POLICY_LABELS = { privacy: 'Privacy', terms: 'Terms', shipping: 'Shipping', returns: 'Returns' };
  var HOME_TITLE = 'VERO — Everyday, considered.';
  var design = !!(window.Shopify && window.Shopify.designMode);

  var qs = function (sel, root) { return (root || document).querySelector(sel); };
  var qsa = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var compact = function () { return window.matchMedia(COMPACT).matches; };
  var reduced = function () { return window.matchMedia(REDUCED).matches; };
  // an element by its id, but never a copy inside a policy's text: a policy pasted from a rendered page carries that
  // page's own ids, and the copy comes first in the document (26 Sep 2026: Add to Cart opened a hidden drawer and froze
  // the page). vero-policy-body.liquid strips such pastes; this is the second guard.
  function outside(sel) { var all = document.querySelectorAll(sel); for (var i = 0; i < all.length; i++) { if (!all[i].closest('.policy-body')) return all[i]; } return null; }
  function byId(id) { return outside('[id="' + id + '"]'); }
  function setAttr(el, name, on) { if (!el) return; if (on) { if (!el.hasAttribute(name)) el.setAttribute(name, ''); } else if (el.hasAttribute(name)) el.removeAttribute(name); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  // the customer events Shopify's own pixel does not send (specification §18): a protocol seen, a protocol added, a newsletter
  // signup — published for the store's custom pixel to forward (SHOPIFY.md: the pixel to paste once GA4 / Meta exist)
  function track(name, data) { try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(name, data || {}); } catch (e) {} }
  function trackPage(page) { if (page && page.indexOf('protocol-') === 0) track('vero_view_protocol', { protocol: page.slice(9) }); }

  // ── data and DOM ────────
  var SCENE, STORE, ITEMS, byHandle, PLATE, ROOT, ORDER;
  var D = null;
  // active/here: the product (the shelf, the camera); page: the view that stands in for the home when there is no product —
  // 'products' (the range as a grid) or 'notfound' — committed with the route like `here` (shelf-context.tsx: pathOf)
  var state = { active: null, here: null, page: null, anchor: null, filter: null, hovered: null, inRoom: false, ready: false, intent: null, pageTitle: '', panel: null };

  function collect() {
    var sceneEl = document.getElementById('vero-scene'), storeEl = document.getElementById('vero-store'), room = document.getElementById('v-room');
    if (!sceneEl || !storeEl || !room) return false;
    try { SCENE = JSON.parse(sceneEl.textContent); STORE = JSON.parse(storeEl.textContent); } catch (e) { return false; }
    ITEMS = SCENE.items; PLATE = SCENE.plate; byHandle = {};
    ITEMS.forEach(function (it) { byHandle[it.handle] = it; });
    ORDER = ITEMS.map(function (it) { return it.handle; });
    ROOT = (STORE.root_url || '/').replace(/\/$/, '');
    var frame = document.getElementById('v-frame');
    D = {
      room: room, scene: qs('#v-scene'), camera: qs('#v-camera'), plate: qs('.v-room__plate', room),
      header: document.getElementById('v-header'), frame: frame,
      home: frame ? frame.querySelector('[data-view="home"]') : null,
      pages: {},
      articles: frame ? qsa('.v-product[data-handle]', frame) : [], articleFor: {}, layers: {},
      panel: byId('v-panel'), cart: byId('v-cart'), cartDim: outside('[data-cart-dim]')
    };
    D.articles.forEach(function (a) { D.articleFor[a.getAttribute('data-handle')] = a; });
    // the page views: the collection, the protocols, about, FAQ, contact, the archive, an article, the 404
    (frame ? qsa('[data-view="page"], [data-view="notfound"]', frame) : []).forEach(function (el) { D.pages[el.getAttribute('data-page') || 'notfound'] = el; });
    ITEMS.forEach(function (it) {
      D.layers[it.handle] = {
        erasable: qsa('.erasable[data-handle="' + it.handle + '"]', room),
        hit: qs('.v-hit[data-handle="' + it.handle + '"]', room),
        veil: qs('.v-veil[data-handle="' + it.handle + '"]', room),
        marker: qs('.v-marker[data-handle="' + it.handle + '"]', room),
        name: qs('.v-plate-name[data-handle="' + it.handle + '"]', room),
        index: qs('.v-index__item[data-handle="' + it.handle + '"]')
      };
    });
    return true;
  }

  // ── geometry (lib/scene.ts) ────────
  function focusPoint(it) { return { x: it.body.left + it.body.width / 2, y: it.body.top + it.body.height / 2 }; }
  function zoomFor(it) { return Math.min(2.0, Math.max(1.6, it.fill / it.body.height)); }
  function eraseOrder(it) { var n = 0; ITEMS.forEach(function (s) { if (!s.guest && s.depth > it.depth) n++; }); return n; }
  function boxes() {
    var vw = window.innerWidth, vh = window.innerHeight, ratio = PLATE.width / PLATE.height;
    if (compact()) { var h = Math.min(1.08 * vw, 0.64 * vh); return { frame: { w: vw, h: h }, scene: { w: h * ratio, h: h } }; }
    var sw = Math.max(vw, vh * ratio);
    return { frame: { w: vw, h: vh }, scene: { w: sw, h: sw / ratio } };
  }
  function clampFocus(focus, scale, b) {
    var hx = b.frame.w / (2 * b.scene.w * scale), hy = b.frame.h / (2 * b.scene.h * scale);
    return { x: Math.min(Math.max(focus.x, hx), 1 - hx), y: Math.min(Math.max(focus.y, hy), 1 - hy) };
  }
  function onShelf(h) { return !!(h && byHandle[h]); } // a product with a place in the photograph (a guest counts: its page is in the room)
  function cameraFor() {
    var chosen = state.active ? byHandle[state.active] : null, b = boxes();
    if (chosen) { var s = compact() ? Math.max(1.5, zoomFor(chosen) * 0.85) : zoomFor(chosen); return { focus: clampFocus(focusPoint(chosen), s, b), scale: s }; }
    if (compact()) return { focus: clampFocus(COMPACT_REST.focus, COMPACT_REST.scale, b), scale: COMPACT_REST.scale };
    return { focus: { x: 0.5, y: 0.5 }, scale: 1 };
  }

  // ── the camera: Motion's spring (stiffness 88, damping 26, mass 1), interruptible ──
  var cam = { x: 0, y: 0, s: 1, vx: 0, vy: 0, vs: 0, tx: 0, ty: 0, ts: 1, ox: 0, oy: 0, os: 1, raf: 0, last: 0, written: '' };
  function targets() {
    var c = cameraFor(), s = state.ready ? c.scale : c.scale * 1.035;
    return { x: -(c.focus.x - 0.5) * s * 100, y: -(c.focus.y - 0.5) * s * 100, s: s, zoom: c.scale };
  }
  function writeCamera() {
    if (!D || !D.camera) return;
    var t = 'translateX(' + cam.x.toFixed(4) + '%) translateY(' + cam.y.toFixed(4) + '%) scale(' + cam.s.toFixed(5) + ')';
    cam.written = t;
    if (D.camera.style.transform !== t) D.camera.style.transform = t;
  }
  function aimCamera(snap) {
    if (!D || !D.camera) return;
    var t = targets();
    if (t.x !== cam.tx || t.y !== cam.ty || t.s !== cam.ts) { cam.ox = cam.x; cam.oy = cam.y; cam.os = cam.s; }
    cam.tx = t.x; cam.ty = t.y; cam.ts = t.s;
    // the markers counter-scale to the destination at once (ProductOnShelf: scale 1/zoom is a style, not an animation)
    var inv = (1 / t.zoom).toFixed(5);
    if (D.camera.style.getPropertyValue('--inv-zoom') !== inv) D.camera.style.setProperty('--inv-zoom', inv);
    if (snap || reduced()) { cam.x = t.x; cam.y = t.y; cam.s = t.s; cam.vx = cam.vy = cam.vs = 0; writeCamera(); stopCamera(); return; }
    // will-change only while the camera moves (Shelf.tsx): left on, Chrome keeps the layer's bitmap at its rest raster and
    // stretches it for the push-in, and the chosen product's label goes soft; dropped at rest, the layer is re-rastered sharp
    if (D.camera.style.willChange !== 'transform') D.camera.style.willChange = 'transform';
    if (!cam.raf) { cam.last = 0; cam.raf = requestAnimationFrame(tick); }
  }
  function stopCamera() { if (cam.raf) cancelAnimationFrame(cam.raf); cam.raf = 0; if (D && D.camera && D.camera.style.willChange !== 'auto') D.camera.style.willChange = 'auto'; }
  function spring(pos, vel, target, h) {
    var a = (-SPRING.stiffness * (pos - target) - SPRING.damping * vel) / SPRING.mass;
    vel += a * h; pos += vel * h;
    return [pos, vel];
  }
  function tick(now) {
    cam.raf = 0;
    var dt = cam.last ? Math.min(0.064, (now - cam.last) / 1000) : 1 / 60; cam.last = now;
    var n = Math.max(1, Math.ceil(dt / (1 / 240))), h = dt / n, r;
    for (var i = 0; i < n; i++) {
      r = spring(cam.x, cam.vx, cam.tx, h); cam.x = r[0]; cam.vx = r[1];
      r = spring(cam.y, cam.vy, cam.ty, h); cam.y = r[0]; cam.vy = r[1];
      r = spring(cam.s, cam.vs, cam.ts, h); cam.s = r[0]; cam.vs = r[1];
    }
    // Motion's rest thresholds: a granular value (a move under 5 units, the scale) rests at
    // speed 0.01 / delta 0.005, a larger one at speed 2 / delta 0.5; each value settles on its own
    var rest = true;
    [['x', 'vx', 'tx', 'ox'], ['y', 'vy', 'ty', 'oy'], ['s', 'vs', 'ts', 'os']].forEach(function (k) {
      var granular = Math.abs(cam[k[2]] - cam[k[3]]) < 5;
      var done = Math.abs(cam[k[1]]) < (granular ? 0.01 : 2) && Math.abs(cam[k[0]] - cam[k[2]]) < (granular ? 0.005 : 0.5);
      if (done) { cam[k[0]] = cam[k[2]]; cam[k[1]] = 0; } else rest = false;
    });
    writeCamera();
    if (!rest) cam.raf = requestAnimationFrame(tick); else stopCamera();
  }

  // ── the shelf (Shelf.tsx / ProductOnShelf.tsx) ────────
  function phaseOf(it) {
    if (state.active) return it.handle === state.active ? 'chosen' : 'erased';
    if (it.guest) return 'erased'; // a guest (LOCK) is not in the photograph at rest: laid on the tray for its own page only
    if (state.filter && it.category !== state.filter) return 'erased';
    return 'idle';
  }
  function paintShelf() {
    if (!D) return;
    var marked = state.ready && (compact() || state.inRoom);
    ITEMS.forEach(function (it) {
      var L = D.layers[it.handle], ph = phaseOf(it), erased = ph === 'erased', idle = ph === 'idle';
      var lit = idle && state.hovered === it.handle;
      var order = String(eraseOrder(it));
      L.erasable.forEach(function (el) { if (el.style.getPropertyValue('--erase-i') !== order) el.style.setProperty('--erase-i', order); setAttr(el, 'data-erased', erased); });
      if (L.hit) {
        setAttr(L.hit, 'data-erased', erased);
        setAttr(L.hit, 'inert', erased);
        // the chosen product's own silhouette is the way back (ProductOnShelf.tsx)
        var label = ph === 'chosen' ? 'Return to the collection' : L.hit.getAttribute('data-label');
        if (label && L.hit.getAttribute('aria-label') !== label) L.hit.setAttribute('aria-label', label);
        L.hit.setAttribute('data-go', ph === 'chosen' ? 'home' : 'product');
        var href = ph === 'chosen' ? (ROOT || '/') : (L.hit.__href || L.hit.getAttribute('href'));
        if (href && L.hit.getAttribute('href') !== href) L.hit.setAttribute('href', href);
      }
      if (L.veil) setAttr(L.veil, 'data-lit', lit);
      if (L.name) setAttr(L.name, 'data-hovered', lit);
      if (L.marker) { setAttr(L.marker, 'data-erased', !idle); setAttr(L.marker, 'data-hovered', lit); }
      if (L.index) {
        setAttr(L.index, 'data-hovered', lit);
        setAttr(L.index, 'data-dim', !!state.hovered && !lit);
        setAttr(L.index, 'data-hidden', !!state.filter && it.category !== state.filter);
      }
    });
    if (D.room.getAttribute('data-active') !== (state.active || '')) D.room.setAttribute('data-active', state.active || '');
    setAttr(D.room, 'data-marked', marked);
    if (D.header) {
      setAttr(D.header, 'data-shelf', !state.active && !state.page);
      if (D.header.getAttribute('data-page') !== (state.page || '')) D.header.setAttribute('data-page', state.page || '');
      // the header stands on white on every sheet: a page, or a product beside the room (Chrome.tsx)
      var onWhite = (!!state.page && state.page !== 'notfound') || (!!state.active && !onShelf(state.active));
      if (D.header.getAttribute('data-ground') !== (onWhite ? 'white' : '')) D.header.setAttribute('data-ground', onWhite ? 'white' : '');
      qsa('.v-header__products', D.header).forEach(function (a) { if (state.page === 'products' && !state.active) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current'); });
      qsa('.v-header__dest[data-key]', D.header).forEach(function (a) {
        // the destination stays current on its own pages (Chrome.tsx: pathname === href || startsWith(href + '/')): a protocol under Protocols, an article under Archive
        var k = a.getAttribute('data-key'), cur = !state.active && !!state.page && (state.page === k || (k === 'protocols' && state.page.indexOf('protocol-') === 0) || (k === 'archive' && state.page.indexOf('article:') === 0));
        if (cur) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
      });
      qsa('button[data-filter]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-filter') === state.filter ? 'true' : 'false'); }); // the segments under the home's index (ShelfFilter.tsx)
    }
  }
  function setHovered(h) { if (state.hovered === h) return; state.hovered = h; paintShelf(); }

  // ── the copy: views, reveals, the frame (RouteFrame.tsx, Reveal.tsx) ──
  var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { rootMargin: '-8% 0px -8% 0px' }) : null;
  function armReveals(view) {
    if (!view) return;
    qsa('[data-reveal]', view).forEach(function (el) { el.classList.remove('is-in'); if (io) io.observe(el); else el.classList.add('is-in'); });
  }
  function replay(view) {
    if (!view) return;
    qsa('.is-in', view).forEach(function (el) { el.classList.remove('is-in'); });
    void view.offsetWidth;
    qsa('.v-home__hero, .v-home__bottom, .v-notfound__inner, .v-product__left, .v-product__right', view).forEach(function (el) { el.classList.add('is-in'); });
    armReveals(view);
  }
  // the copy shows the committed route; during an intent the shelf and camera are already on their way
  function viewHandle() { return state.intent ? state.here : state.active; }
  function pageView() { return state.page && D.pages[state.page] ? D.pages[state.page] : null; }
  function isPageView(el) { return !!el && (el.getAttribute('data-view') === 'page' || el.getAttribute('data-view') === 'notfound'); }
  function currentView() { var h = viewHandle(); return h ? D.articleFor[h] : (pageView() || D.home); }
  function syncView() {
    if (!D || !D.frame) return;
    var handle = viewHandle(), page = handle ? null : (pageView() ? state.page : null);
    setAttr(D.home, 'hidden', !!handle || !!page);
    Object.keys(D.pages).forEach(function (k) { if (D.pages[k]) setAttr(D.pages[k], 'hidden', page !== k); });
    D.articles.forEach(function (a) { setAttr(a, 'hidden', a.getAttribute('data-handle') !== handle); });
    var view = currentView();
    if (view) qsa('.v-home__hero, .v-home__bottom, .v-notfound__inner, .v-product__left, .v-product__right', view).forEach(function (el) { el.classList.add('is-in'); });
    var title = handle && D.articleFor[handle] ? D.articleFor[handle].getAttribute('data-title') : (page ? (D.pages[page].getAttribute('data-title') || state.pageTitle) : HOME_TITLE);
    if (page && state.anchor) { var target = document.getElementById(state.anchor); state.anchor = null; if (target) setTimeout(function () { target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' }); }, 40); }
    if (title && document.title !== title) document.title = title;
  }

  // Navigation is a choreography, not a swap (shelf-context.tsx): the shelf
  // starts erasing and the outgoing copy clears on the click; the route
  // commits 260 ms later, while the product is still travelling.
  var timer = 0;
  function trim(u) { return String(u || '').replace(/[?#].*$/, '').replace(/\/$/, ''); }
  function archivePath() { return trim(STORE.archive_url || (ROOT + '/blogs/archive')); }
  // the address of a page view: the store's own for the pages it has, the article's for an article
  function urlFor(handle, page) {
    if (handle) { var p = STORE.products && STORE.products[handle]; return (p && p.url) || (ROOT + '/products/' + handle); }
    // an article's key carries the blog's handle, as Liquid's article.handle does ("archive/the-ritual-matters"): its
    // address is the store's own for it, else /blogs/ + that key (26 Sep 2026 — it used to be built under the archive's
    // address, /blogs/archive/archive/…, a 404, and the cards fell back to a full navigation there)
    if (page && page.indexOf('article:') === 0) { var ah = page.slice(8), aa = (STORE.articles || []).filter(function (a) { return a.handle === ah; })[0]; return (aa && aa.url) ? trim(aa.url) : ROOT + '/blogs/' + ah; }
    if (page && STORE.pages && STORE.pages[page]) return trim(STORE.pages[page]);
    if (page === 'products') return ROOT + '/collections/all';
    if (page) return ROOT + '/pages/' + page;
    return (ROOT || '/') + (state.filter ? '?c=' + state.filter : '');
  }
  // an article's view is not in the page until it is asked for: fetched as a fragment and mounted (vero-fragment.liquid)
  function ensureView(page) {
    if (!page || D.pages[page]) return Promise.resolve(true);
    var url = urlFor(null, page);
    return fetch(url + (url.indexOf('?') >= 0 ? '&' : '?') + 'view=fragment', { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var view = doc.querySelector('[data-view="page"]');
        if (!view || !D.frame) throw new Error('no view');
        view.setAttribute('hidden', '');
        D.frame.appendChild(view);
        D.pages[view.getAttribute('data-page') || page] = view;
        bindStrips(); bindTileStrips(); armReveals(view);
        return true;
      })
      .catch(function () { return false; });
  }
  // a product, the home (null) or a page without a product (null, 'products'): routes without a slug are told
  // apart by their path, so the wordmark leaves the products page and the Products link leaves the home
  function go(handle, page, anchor) {
    page = handle ? null : (page || null);
    // a link to the page already open goes to its section, or back to the top (shelf-context.tsx go)
    var shopOpen = D.header && qs('[data-shop]', D.header); if (shopOpen) shopOpen.removeAttribute('data-open');
    if (handle === state.here && page === state.page && !state.intent) {
      setPanel(null);
      var target = anchor ? document.getElementById(anchor) : null;
      if (target) target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
      else if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' });
      return;
    }
    if (state.intent && state.intent.target === handle && state.intent.page === page) return;
    state.hovered = null;
    setPanel(null);
    state.intent = { target: handle, page: page };
    state.anchor = anchor || null;
    state.active = handle;
    var url = urlFor(handle, page), ready = ensureView(page);
    // React commits the intent and Motion schedules its animations on the following frames;
    // the shelf and the camera set off two frames after the click, as they do on the site
    requestAnimationFrame(function () { requestAnimationFrame(function () { if (state.active === handle) { paintShelf(); aimCamera(false); } }); });
    // a link to a section goes there in one move once the page is in, instead of to the top and back down
    if (!anchor && window.scrollY > 0) window.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' });
    if (D.frame) D.frame.setAttribute('data-leaving', '');
    clearTimeout(timer);
    // the route commits at 260 ms — and, for a view that had to be fetched, once it is here; a view
    // that cannot be fetched is navigated to the ordinary way
    timer = setTimeout(function () { ready.then(function (ok) { if (state.intent && state.intent.target === handle && state.intent.page === page) { if (ok) commit(); else location.href = url; } }); }, 260);
  }
  function commit() {
    var handle = state.intent ? state.intent.target : state.active, page = state.intent ? state.intent.page : null;
    state.intent = null;
    state.here = handle; state.page = page;
    try { history.pushState({ vero: true, handle: handle, page: page, filter: state.filter }, '', urlFor(handle, page)); } catch (e) {}
    syncView(); trackPage(page);
    replay(currentView());
    if (D.frame) D.frame.removeAttribute('data-leaving');
    lastY = window.scrollY; lastUp = false;
    standing();
  }
  function arrive(handle, page) { // back/forward: the route is already there, the copy simply mounts
    clearTimeout(timer); state.intent = null; state.hovered = null;
    page = handle ? null : (page || null);
    ensureView(page).then(function (ok) {
      if (!ok) { location.reload(); return; }
      state.active = handle; state.here = handle; state.page = page;
      paintShelf(); aimCamera(false); syncView(); replay(currentView()); trackPage(page);
      if (D.frame) D.frame.removeAttribute('data-leaving');
      standing();
    });
  }
  function routeFromUrl() { // { handle, page } for a route the room can mount, undefined for anything else
    var m = PRODUCT_RE.exec(location.pathname);
    if (m) { var h = decodeURIComponent(m[1]); return D && D.articleFor[h] ? { handle: h, page: null } : undefined; }
    var path = trim(location.pathname);
    if (path === ROOT || path === '') return { handle: null, page: null };
    var keys = Object.keys(STORE.pages || {});
    for (var i = 0; i < keys.length; i++) { if (trim(STORE.pages[keys[i]]) === path && (D.pages[keys[i]] || keys[i])) return { handle: null, page: keys[i] }; }
    // /blogs/<blog>/<article> is the article keyed "article:<blog>/<article>"; /blogs/<blog> alone is a page above
    var a = path.indexOf(ROOT + '/blogs/') === 0 ? path.slice((ROOT + '/blogs/').length) : '';
    if (a && a.split('/').length === 2) return { handle: null, page: 'article:' + decodeURIComponent(a) };
    return undefined;
  }
  function setFilter(f) {
    state.hovered = null;
    state.filter = f;
    paintShelf();
    if (!state.active) { try { history.pushState({ vero: true, handle: null, filter: f }, '', urlFor(null)); } catch (e) {} }
  }

  // ── header standing (Chrome.tsx useStanding) ────────
  var lastY = 0, lastUp = false, raf = 0;
  function standing() {
    raf = 0;
    if (!D || !D.header) return;
    if ('scrollLock' in document.body.dataset) return; // a sheet has the page held in place: nothing moved
    var y = window.scrollY, up = lastUp;
    if (y !== lastY) { up = y < lastY; lastY = y; lastUp = up; }
    // the route's paper mark (id="paper" on the site): a product's paper band, a page, the home's white sheet
    var view = state.active ? D.articleFor[state.active] : (pageView() || D.home);
    var paper = view ? (view.hasAttribute('data-paper') ? view : view.querySelector('[data-paper]')) : null;
    var under = !!paper && paper.getBoundingClientRect().top <= 0;
    var s;
    // the band layout, and every layout once the sheet is beneath the header: hidden on the way down, on a ground of
    // the sheet's tone on the way up (Chrome.tsx, 21 Sep 2026 — the desktop used to stand groundless on the paper)
    if (compact() || under) s = y <= 32 ? 'room' : up ? 'sheet' : 'away';
    else if (!paper) s = y > 32 && !up ? 'away' : 'room';
    else s = y > 32 ? 'away' : 'room';
    if (D.header.getAttribute('data-standing') !== s) D.header.setAttribute('data-standing', s);
    // white wherever the header stands on the room or on the band layout's faded-black sheet (Chrome.tsx): the home at
    // rest, a product's shaded flanks, the 404, and the bar on the way back up a phone until the light sheet has passed
    // beneath it; never on a sheet from the top — a page, or a product beside the room (LOCK)
    // the set page opens on the room too (the client, 2 Oct 2026: the wordmark in white there)
    var onRoomRoute = state.active ? onShelf(state.active) : (!state.page || state.page === 'notfound' || state.page === 'set');
    D.header.classList.toggle('on-shade', onRoomRoute && (s === 'room' || (s === 'sheet' && !under)));
    // the bar's ground on the way back up: the light sheet once it is beneath the header (white on the home, the paper on
    // a product), the faded black before that; a page's is its own white throughout
    var bar = !onRoomRoute ? 'white' : under ? ((!state.active && (!state.page || state.page === 'set')) ? 'white' : 'paper') : 'night';
    if (D.header.getAttribute('data-bar') !== bar) D.header.setAttribute('data-bar', bar);
    // aria-hidden wants the word, not the bare attribute setAttr writes (an empty value is not "true" to a screen reader)
    if (s === 'away') D.header.setAttribute('aria-hidden', 'true'); else D.header.removeAttribute('aria-hidden');
    var mark = qs('.v-header__wordmark', D.header);
    if (mark) { if (s === 'away') mark.setAttribute('tabindex', '-1'); else mark.removeAttribute('tabindex'); }
  }
  function onScroll() { if (!raf) raf = requestAnimationFrame(standing); }

  // ── sheets: the panel (Panel.tsx) and the cart (CartDrawer.tsx) ──
  // the page under a sheet (lib/scroll-lock.ts): overflow hidden is enough for a mouse; a finger on iOS still drags the
  // document behind a fixed sheet, so where the pointer is coarse the body is fixed at its scroll position and put back
  var locks = 0, saved = null, LOCK_KEYS = ['overflow', 'position', 'top', 'left', 'right', 'width'];
  function lock(on) {
    var was = locks; locks = Math.max(0, locks + (on ? 1 : -1));
    var html = document.documentElement, body = document.body;
    if (!was && locks) {
      saved = { y: window.scrollY, fixed: window.matchMedia('(pointer: coarse)').matches, html: html.style.overflow, body: {} };
      LOCK_KEYS.forEach(function (k) { saved.body[k] = body.style[k]; });
      html.style.overflow = 'hidden'; body.style.overflow = 'hidden';
      if (saved.fixed) { body.style.position = 'fixed'; body.style.top = -saved.y + 'px'; body.style.left = '0'; body.style.right = '0'; body.style.width = '100%'; }
      body.dataset.scrollLock = '';
    } else if (was && !locks && saved) {
      html.style.overflow = saved.html;
      LOCK_KEYS.forEach(function (k) { body.style[k] = saved.body[k]; });
      delete body.dataset.scrollLock;
      if (saved.fixed) window.scrollTo(0, saved.y);
      saved = null;
    }
  }
  // the client's quiz (snippets/vero-routine-quiz.liquid) is its own drawer and asks for the same lock: detail true, then false
  document.addEventListener('vero:lock', function (e) { lock(!!e.detail); });
  var FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';
  function trap(dialog) {
    return function (e) {
      if (e.key !== 'Tab') return;
      var nodes = qsa(FOCUSABLE, dialog).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
      if (!nodes.length) return;
      var first = nodes[0], last = nodes[nodes.length - 1], active = document.activeElement;
      if (e.shiftKey && (active === first || !dialog.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
  }
  var panelReturn = null, panelTrap = null, panelTimer = 0;
  function setPanel(key) {
    if (state.panel === key) return;
    var panel = D && D.panel; if (!panel) return;
    var wasOpen = !!state.panel;
    state.panel = key;
    qsa('[data-panel]').forEach(function (b) { b.setAttribute('aria-expanded', key && b.getAttribute('data-panel') === key ? 'true' : 'false'); });
    if (key) {
      qsa('[data-panel-body]', panel).forEach(function (b) { setAttr(b, 'hidden', b.getAttribute('data-panel-body') !== key); });
      var label = qs('[data-panel-label]', panel);
      if (label) label.textContent = key === 'science' ? 'Rooted in Nature, Refined by Science' : key === 'menu' ? 'VERO' : key === 'search' ? 'Search' : 'Policies';
      panel.setAttribute('aria-label', key === 'science' ? 'Science' : key === 'menu' ? 'Menu' : key === 'search' ? 'Search' : POLICY_LABELS[key] || key);
      clearTimeout(panelTimer);
      panel.removeAttribute('hidden');
      void panel.offsetWidth;
      panel.setAttribute('data-open', '');
      panel.scrollTop = 0;
      if (!wasOpen) {
        panelReturn = document.activeElement;
        lock(true);
        panelTrap = trap(panel); panel.addEventListener('keydown', panelTrap);
      }
      // search puts the keyboard in its own field (Panel.tsx)
      var field = key === 'search' ? qs('[data-search-input]', panel) : null;
      if (field) { field.focus({ preventScroll: true }); renderSearch(field.value); }
      else { var close = qs('[data-panel-close]', panel); if (close) close.focus({ preventScroll: true }); }
    } else {
      panel.removeAttribute('data-open');
      if (panelTrap) panel.removeEventListener('keydown', panelTrap);
      lock(false);
      if (panelReturn && panelReturn.focus) panelReturn.focus({ preventScroll: true });
      panelReturn = null;
      panelTimer = setTimeout(function () { if (!state.panel) panel.setAttribute('hidden', ''); }, 720);
    }
  }

  // Site-wide discovery (lib/search.ts): products, protocols and the archive, every word of the query
  // somewhere in what the entry answers to; grouped PRODUCTS / PROTOCOLS / ARCHIVE as it is typed.
  function searchIndex() {
    var out = [];
    Object.keys(SCENE.range || {}).forEach(function (h) {
      var r = SCENE.range[h], sp = STORE.products[h];
      out.push({ group: 'Products', title: r.no + ' ' + r.name, sub: r.descriptor + ' · ' + r.volume, href: (sp && sp.url) || (ROOT + '/products/' + h), handle: h, terms: r.terms || '' });
    });
    if (SCENE.set) out.push({ group: 'Products', title: SCENE.set.title, sub: SCENE.set.sub, href: urlFor(null, 'set'), page: 'set', terms: SCENE.set.terms || '' });
    (SCENE.protocols || []).forEach(function (p) { out.push({ group: 'Protocols', title: p.name, sub: p.pair + ' — ' + p.outcome, href: urlFor(null, 'protocol-' + p.slug), page: 'protocol-' + p.slug, terms: p.terms || '' }); });
    (STORE.articles || []).forEach(function (a) { out.push({ group: 'Archive', title: a.title, sub: a.excerpt || '', href: a.url, page: 'article:' + a.handle, terms: [a.title, a.excerpt || '', (a.tags || []).join(' '), 'archive article'].join(' ').toLowerCase() }); });
    return out;
  }
  var INDEX = null;
  function search(q) {
    var words = String(q || '').toLowerCase().replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
    if (!words.length) return [];
    if (!INDEX) INDEX = searchIndex();
    return INDEX.filter(function (e) { return words.every(function (w) { return e.terms.indexOf(w) >= 0; }); });
  }
  function renderSearch(q) {
    var box = qs('[data-search-results]'); if (!box) return;
    var hits = search(q), asked = String(q || '').trim().length > 0;
    if (!asked) { box.innerHTML = ''; return; }
    if (!hits.length) { box.innerHTML = '<p class="t-body v-small v-ink70">Nothing found. Try another search.</p>'; return; }
    box.innerHTML = ['Products', 'Protocols', 'Archive'].map(function (g) {
      var rows = hits.filter(function (h) { return h.group === g; });
      if (!rows.length) return '';
      return '<section class="v-search__group" aria-label="' + g + '"><h2 class="t-label v-ink45">' + g + '</h2><ul class="v-search__list">' + rows.map(function (r) {
        return '<li class="v-search__item"><a class="v-search__link" href="' + esc(r.href) + '"' + (r.handle ? ' data-handle="' + esc(r.handle) + '"' : ' data-go="page:' + esc(r.page) + '"') + '><span class="v-search__title">' + esc(r.title) + '</span><span class="t-spec v-ink45 v-nocase">' + esc(r.sub) + '</span></a></li>';
      }).join('') + '</ul></section>';
    }).join('');
  }
  document.addEventListener('input', function (e) { if (e.target && e.target.matches && e.target.matches('[data-search-input]')) renderSearch(e.target.value); });
  document.addEventListener('submit', function (e) {
    var f = e.target; if (f && f.querySelector && f.querySelector('input[name="contact[tags]"][value="newsletter"]')) track('vero_newsletter_signup', {});
  });
  document.addEventListener('submit', function (e) {
    var form = e.target; if (!form || !form.matches || !form.matches('[data-search-form]')) return;
    var first = qs('.v-search__link', form.parentNode);
    if (first) { e.preventDefault(); first.click(); }
  });

  // The cart: Shopify's, through the Ajax API. Only the count is optimistic
  // (cart-context.tsx): it answers the click; the lines wait for the store.
  var cartState = { cart: null, count: null, pending: false, error: null, open: false, returnTo: null, trap: null, timer: 0 };
  function money(cents) {
    var fmt = STORE.money_format || '{{amount}}';
    var m = /\{\{\s*(\w+)\s*\}\}/.exec(fmt); var kind = m ? m[1] : 'amount';
    var v = (Number(cents) || 0) / 100, out;
    function sep(n, dec, th, dp) { var parts = n.toFixed(dec).split('.'); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th); return dp !== undefined && parts[1] ? parts[0] + dp + parts[1] : parts[0]; }
    switch (kind) {
      case 'amount_no_decimals': out = sep(v, 0, ','); break;
      case 'amount_with_comma_separator': out = sep(v, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = sep(v, 0, '.'); break;
      case 'amount_with_apostrophe_separator': out = sep(v, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': out = sep(v, 0, ' '); break;
      case 'amount_with_space_separator': out = sep(v, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': out = sep(v, 2, ' ', '.'); break;
      default: out = sep(v, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]+>/g, '');
  }
  function handleOfLine(line) { return line.handle || (line.url && (PRODUCT_RE.exec(line.url) || [])[1]) || null; }
  function nameOf(handle) { return (SCENE.range && SCENE.range[handle]) || byHandle[handle] || null; }
  function count() { return cartState.count !== null ? cartState.count : (cartState.cart ? cartState.cart.item_count : Number((qs('[data-cart-count]') || {}).textContent) || 0); }
  // CONTINUE THE ROUTINE (lib/pairings.ts recommendForCart, specification §04/§11): for every product in the cart, the
  // other half of each of its protocols unless it is already there, the reason being the protocol's own name and
  // outcome; a product wanted by two protocols comes first, then the range's order
  function recommend(lines) {
    var inCart = {}, score = {}, range = Object.keys(SCENE.range || {});
    lines.forEach(function (l) { var h = handleOfLine(l); if (h) inCart[h] = true; });
    lines.forEach(function (l) {
      var h = handleOfLine(l); if (!h) return;
      (SCENE.protocols || []).forEach(function (p) {
        if (p.products.indexOf(h) < 0) return;
        var other = p.products[0] === h ? p.products[1] : p.products[0];
        if (inCart[other]) return;
        var cur = score[other]; score[other] = { n: (cur ? cur.n : 0) + 1, reason: cur ? cur.reason : p.name + ' — ' + p.outcome };
      });
    });
    return Object.keys(score).sort(function (a, b) { return score[b].n - score[a].n || range.indexOf(a) - range.indexOf(b); }).slice(0, 2).map(function (h) { return { handle: h, reason: score[h].reason }; });
  }
  function renderCart() {
    var cartEl = D && D.cart; if (!cartEl) return;
    var cart = cartState.cart, lines = cart ? cart.items : [], n = count(), pending = cartState.pending;
    var unavailableLine = function (l) { var sp = STORE.products[handleOfLine(l)]; return !!(sp && sp.exists && !sp.available); };
    var anyUnavailable = lines.some(unavailableLine);
    var linesEl = qs('[data-cart-lines]', cartEl);
    if (linesEl) linesEl.innerHTML = lines.map(function (l) {
      var h = handleOfLine(l), it = nameOf(h);
      var img = l.image ? l.image.replace(/(\.[a-z0-9]+)(\?|$)/i, '_224x$1$2') : '';
      // a product's line carries its packshot; the set's carries the set product's own image (CartDrawer.tsx: /set/packshot.jpg)
      return '<li class="v-line" data-key="' + esc(l.key) + '">' +
        ((it || h === 'the-set') && img ? '<div class="v-line__thumb"><img src="' + esc(img) + '" alt="" loading="lazy"></div>' : '') +
        '<div class="v-line__body"><div class="v-line__row"><p class="v-line__title">' + esc(it ? it.no + ' ' + it.name : l.product_title) + '</p>' +
        // a protocol price comes off one of its two products (SHOPIFY.md, "Protocol prices"): that line shows what it was,
        // struck through, and names the protocol under it, as checkout will (CartDrawer.tsx)
        '<p class="v-price v-line__price">' + (l.original_line_price > l.final_line_price ? '<s class="v-line__was"><span class="v-sr">Was </span>' + esc(money(l.original_line_price)) + '</s><span class="v-sr">, now </span>' : '') + esc(money(l.final_line_price)) + (l.quantity > 1 ? '<span class="v-line__unit">' + l.quantity + ' × ' + esc(money(l.original_price)) + '</span>' : '') + '</p></div>' +
        (it ? '<p class="t-spec v-ink45 v-mt1">' + esc(it.descriptor + ' · ' + it.volume) + '</p>' : '') +
        (l.line_level_discount_allocations || []).filter(function (a) { return a.amount > 0; }).map(function (a) { return '<p class="t-spec v-line__deal">' + esc(a.discount_application.title) + '</p>'; }).join('') +
        (unavailableLine(l) ? '<p class="t-label v-line__unavailable">Currently unavailable — remove it to check out</p>' : '') +
        '<div class="v-qty"><button type="button" data-qty="-1" aria-label="Decrease quantity">−</button><span class="v-qty__n">' + l.quantity + '</span><button type="button" data-qty="1" aria-label="Increase quantity">+</button><button type="button" class="v-qty__remove" data-qty="0">Remove</button></div>' +
        '</div></li>';
    }).join('');
    setAttr(qs('[data-cart-empty]', cartEl), 'hidden', !(lines.length === 0 && n === 0 && !pending));
    setAttr(qs('[data-cart-adding]', cartEl), 'hidden', !(lines.length === 0 && n > 0 && !cartState.error));
    var routine = qs('[data-cart-routine]', cartEl), list = qs('[data-cart-routine-list]', cartEl);
    var picks = lines.length ? recommend(lines).filter(function (p) { return STORE.products[p.handle]; }) : [];
    if (routine && list) {
      list.innerHTML = picks.map(function (p) {
        var sp = STORE.products[p.handle], it = byHandle[p.handle], can = sp.exists && sp.available && sp.variant_id;
        return '<li>' + (sp.image ? '<div class="v-line__thumb"><img src="' + esc(sp.image) + '" alt="" loading="lazy"></div>' : '<div></div>') +
          '<div><p class="v-pairs__title"><span class="t-spec v-ink45">' + esc(it ? it.no : '') + '</span><span class="v-index__name v-ink">' + esc(it ? it.name : sp.title) + '</span></p>' +
          '<p class="t-body v-small v-ink70 v-mt2">' + esc(p.reason) + '</p>' +
          '<div class="v-mt2"><button type="button" class="v-addpair t-label" data-add="' + esc(sp.variant_id || '') + '" data-name="' + esc(it ? it.no + ' ' + it.name : sp.title) + '"' + (can ? '' : ' disabled data-unavailable') + '>' + (can ? 'Add' : 'Unavailable') + (sp.price_text ? ' <span class="v-addpair__price">' + esc(sp.price_text) + '</span>' : '') + '<span class="v-sr"> — ' + esc(it ? it.no + ' ' + it.name : sp.title) + ', to cart</span>' + '</button></div></div></li>';
      }).join('');
      setAttr(routine, 'hidden', !picks.length);
    }
    var err = qs('[data-cart-notice]', cartEl);
    if (err) { if (cartState.error === LOAD_ERROR) err.innerHTML = esc(LOAD_ERROR) + ' <a href="' + esc(ROOT + '/cart') + '">Open the cart page</a>'; else err.textContent = cartState.error || ''; setAttr(err, 'hidden', !cartState.error); }
    // the subtotal reads as a dash until there is something to add up (CartDrawer.tsx: PLACEHOLDER_PRICE_TEXT)
    var sub = qs('[data-cart-subtotal]', cartEl); if (sub && cart) { var total = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price; sub.textContent = total > 0 ? money(total) : '—'; }
    // without a readable cart the store's own count decides, so checkout stays reachable (the link is the store's cart route);
    // a disabled link also leaves the Tab order, so Enter cannot open an empty checkout, which Shopify bounces back
    var co = qs('[data-cart-checkout]', cartEl), can = (cart ? lines.length > 0 : n > 0) && !pending && !anyUnavailable;
    if (co) { co.classList.toggle('v-cta--disabled', !can); if (can) { co.removeAttribute('aria-disabled'); co.removeAttribute('tabindex'); } else { co.setAttribute('aria-disabled', 'true'); co.setAttribute('tabindex', '-1'); } }
    // in the theme editor (vero-cart.liquid) the link opens a new tab, as a cart permalink of these very lines
    if (co && design && cart && cart.items && cart.items.length) co.setAttribute(co.tagName === 'A' ? 'href' : 'data-href', ROOT + '/cart/' + cart.items.map(function (l) { return l.variant_id + ':' + l.quantity; }).join(','));
    qsa('[data-cart-count]').forEach(function (el) { el.textContent = String(n); });
    qsa('[data-cart-toggle]').forEach(function (b) { b.setAttribute('aria-label', 'Cart, ' + n + ' item' + (n === 1 ? '' : 's')); b.setAttribute('aria-expanded', cartState.open ? 'true' : 'false'); });
    qsa('button[data-add], button[data-add-protocol]').forEach(function (b) {
      var can = !!(b.getAttribute('data-add') || b.getAttribute('data-add-protocol')) && !b.hasAttribute('data-unavailable');
      if (pending) { b.setAttribute('disabled', ''); b.setAttribute('aria-busy', 'true'); } else { b.removeAttribute('aria-busy'); if (can) b.removeAttribute('disabled'); }
      // only the visible words change: the protocol buttons carry a screen-reader span after them (vero-protocols.liquid)
      if (b.classList.contains('v-cta')) { var label = pending ? 'Adding…' : (can ? 'Add to Cart' : 'Currently unavailable'), t = b.firstChild; if (t && t.nodeType === 3) { if (t.nodeValue !== label) t.nodeValue = label; } else b.insertBefore(document.createTextNode(label), b.firstChild); }
    });
  }
  function request(path, body) {
    return fetch(ROOT + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: body ? JSON.stringify(body) : undefined })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) { var err = new Error(j.description || j.message || 'The store did not answer.'); err.data = j; throw err; } return j; }); });
  }
  function settle(c) { cartState.cart = c; cartState.count = c.item_count; cartState.pending = false; renderCart(); }
  function run(work, optimistic) {
    cartState.count = optimistic; cartState.pending = true; cartState.error = null; renderCart();
    return work().then(function (c) { if (c && c.items) settle(c); else return request('/cart.js').then(settle); })
      .catch(function (err) { cartState.error = err.message; cartState.pending = false; return request('/cart.js').then(function (c) { cartState.cart = c; cartState.count = c.item_count; renderCart(); }).catch(function () { cartState.count = null; renderCart(); }); });
  }
  // the cart could not be read (a slow network, a bot check answering HTML): the drawer says so and points at the cart
  // page, and the store's own count keeps checkout reachable — not "Adding…" for ever behind a dead link (26 Sep 2026 review)
  var LOAD_ERROR = 'The cart could not be loaded.';
  function loadCart() {
    return request('/cart.js')
      .then(function (c) { cartState.cart = c; cartState.count = c.item_count; if (cartState.error === LOAD_ERROR) cartState.error = null; renderCart(); })
      .catch(function () { cartState.error = LOAD_ERROR; renderCart(); });
  }
  function add(variantId, quantity) {
    quantity = quantity || 1;
    if (!variantId) { cartState.error = 'That product is not available yet.'; renderCart(); return; }
    run(function () { return request('/cart/add.js', { items: [{ id: Number(variantId), quantity: quantity }] }).then(function () { return request('/cart.js'); }); }, count() + quantity);
  }
  function addMany(ids) {
    ids = ids.filter(Boolean);
    if (!ids.length) { cartState.error = 'That protocol is not available yet.'; renderCart(); return; }
    run(function () { return request('/cart/add.js', { items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }) }).then(function () { return request('/cart.js'); }); }, count() + ids.length);
  }
  function update(key, quantity) {
    var line = null; (cartState.cart ? cartState.cart.items : []).forEach(function (l) { if (l.key === key) line = l; });
    var delta = line ? quantity - line.quantity : 0;
    run(function () { return request('/cart/change.js', { id: key, quantity: quantity }); }, Math.max(0, count() + delta));
  }
  function openCart() {
    var cartEl = D && D.cart; if (!cartEl) return;
    if (cartState.open) return;
    cartState.open = true;
    cartState.returnTo = document.activeElement;
    clearTimeout(cartState.timer);
    cartEl.removeAttribute('hidden'); if (D.cartDim) D.cartDim.removeAttribute('hidden');
    void cartEl.offsetWidth;
    cartEl.setAttribute('data-open', ''); if (D.cartDim) D.cartDim.setAttribute('data-open', '');
    lock(true);
    cartState.trap = trap(cartEl); cartEl.addEventListener('keydown', cartState.trap);
    var close = qs('[data-cart-close]', cartEl); if (close) close.focus({ preventScroll: true });
    renderCart();
    if (!cartState.cart && !cartState.pending) loadCart();
  }
  function closeCart() {
    var cartEl = D && D.cart; if (!cartEl || !cartState.open) return;
    cartState.open = false;
    cartEl.removeAttribute('data-open'); if (D.cartDim) D.cartDim.removeAttribute('data-open');
    cartEl.removeEventListener('keydown', cartState.trap);
    lock(false);
    if (cartState.returnTo && cartState.returnTo.focus) cartState.returnTo.focus({ preventScroll: true });
    cartState.returnTo = null;
    renderCart();
    cartState.timer = setTimeout(function () { if (!cartState.open) { cartEl.setAttribute('hidden', ''); if (D.cartDim) D.cartDim.setAttribute('hidden', ''); } }, 620);
  }

  // ── events, delegated from the document so re-rendered markup keeps working ──
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    var el = e.target.closest('button[data-filter], [data-panel], [data-panel-close], [data-cart-toggle], [data-cart-close], [data-cart-dim], [data-cart-checkout], button[data-add], button[data-add-protocol], [data-qty], a[data-handle], a[data-go]');
    if (!el || !D) return;
    // the checkout link is the browser's own navigation — unless it is disabled, when Enter must not follow it either
    if (el.hasAttribute('data-cart-checkout')) { if (el.getAttribute('aria-disabled') === 'true') e.preventDefault(); return; }
    if (el.hasAttribute('data-filter')) { var f = el.getAttribute('data-filter'); setFilter(state.filter === f ? null : f); return; }
    if (el.hasAttribute('data-panel-close')) { setPanel(null); return; }
    if (el.hasAttribute('data-panel') && !el.hasAttribute('data-panel-body')) { var key = el.getAttribute('data-panel'); setPanel(state.panel === key ? null : key); return; }
    if (el.hasAttribute('data-cart-toggle')) { if (cartState.open) closeCart(); else openCart(); return; }
    if (el.hasAttribute('data-cart-close') || el.hasAttribute('data-cart-dim')) { closeCart(); return; }
    if (el.hasAttribute('data-add')) { e.preventDefault(); if (cartState.pending) return; add(el.getAttribute('data-add'), 1); openCart(); return; }
    if (el.hasAttribute('data-add-protocol')) { e.preventDefault(); if (cartState.pending) return; track('vero_bundle_add', { protocol: el.getAttribute('data-name') || '' }); addMany(el.getAttribute('data-add-protocol').split(',')); openCart(); return; }
    if (el.hasAttribute('data-qty')) {
      var li = el.closest('[data-key]'); if (!li || !cartState.cart || cartState.pending) return;
      var k = li.getAttribute('data-key'), line = null;
      cartState.cart.items.forEach(function (l) { if (l.key === k) line = l; });
      if (!line) return;
      var d = Number(el.getAttribute('data-qty'));
      if (d === 0) { update(k, 0); var c = qs('[data-cart-close]', D.cart); if (c) c.focus({ preventScroll: true }); }
      else update(k, Math.max(0, line.quantity + d));
      return;
    }
    // the room's own links: a modified click (new tab) and the theme editor keep the browser's navigation
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || design) return;
    // a link inside the cart (the empty cart's Explore the collection) closes it on the way (CartDrawer.tsx)
    if (cartState.open && D.cart && D.cart.contains(el)) closeCart();
    if (el.getAttribute('data-go') === 'home') {
      if (!D.frame) return;
      e.preventDefault(); go(null); return;
    }
    var dest = el.getAttribute('data-go') || '';
    if (dest === 'products' || dest.indexOf('page:') === 0) {
      var key = dest === 'products' ? 'products' : dest.slice(5);
      if (!D.frame) return;
      // a page the room does not carry goes the ordinary way (and so does an article that cannot be fetched)
      if (!D.pages[key] && key.indexOf('article:') !== 0) return;
      e.preventDefault(); go(null, key, el.getAttribute('data-anchor') || null); return;
    }
    var h = el.getAttribute('data-handle');
    if (!h || !D.articleFor[h]) return;
    e.preventDefault();
    go(h);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (cartState.open) closeCart(); else if (state.panel) setPanel(null);
  });
  window.addEventListener('popstate', function () {
    if (!D) return;
    var r = routeFromUrl();
    if (!r) return;
    var p = new URLSearchParams(location.search).get('c');
    state.filter = (p === 'shower' || p === 'style') ? p : null;
    if (r.handle !== state.active || r.page !== state.page || state.intent) arrive(r.handle, r.page); else paintShelf();
  });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () { if (!D) return; aimCamera(false); paintShelf(); onScroll(); });

  // ── binding what has to be bound to its element ────────
  function bindStrips() {
    qsa('[data-strip]').forEach(function (strip) {
      if (strip.__vero) return; strip.__vero = true;
      var dots = strip.parentNode.querySelectorAll('.v-dots li');
      strip.addEventListener('scroll', function () {
        var i = Math.round(strip.scrollLeft / strip.clientWidth);
        dots.forEach(function (d, k) { setAttr(d, 'data-on', k === i); });
      }, { passive: true });
    });
  }
  // the home's Style row as a strip (ProductsGrid.tsx TileStrip, 21 Sep 2026): dots for the stops, arrows a tile at a time
  function bindTileStrips() {
    qsa('[data-tile-strip]').forEach(function (wrap) {
      if (wrap.__vero) return; wrap.__vero = true;
      var strip = qs('ul', wrap), dots = qs('.v-dots--strip', wrap), bar = qs('.v-tile-strip__bar', wrap), prev = qs('[data-strip-prev]', wrap), next = qs('[data-strip-next]', wrap);
      if (!strip) return;
      function step() { var first = strip.firstElementChild; return first ? first.getBoundingClientRect().width + 2 : strip.clientWidth; }
      function paint() {
        var w = step(); if (!w) return;
        var n = strip.children.length, per = Math.max(1, Math.round(strip.clientWidth / w)), stops = Math.max(1, n - per + 1), at = Math.max(0, Math.min(stops - 1, Math.round(strip.scrollLeft / w)));
        if (bar) setAttr(bar, 'hidden', stops <= 1);
        if (dots) { if (dots.children.length !== stops) { dots.innerHTML = ''; for (var i = 0; i < stops; i++) dots.appendChild(document.createElement('li')); } qsa('li', dots).forEach(function (d, k) { setAttr(d, 'data-on', k === at); }); }
        if (prev) prev.disabled = at <= 0;
        if (next) next.disabled = at >= stops - 1;
      }
      strip.addEventListener('scroll', paint, { passive: true });
      window.addEventListener('resize', paint);
      if (prev) prev.addEventListener('click', function () { strip.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { strip.scrollBy({ left: step(), behavior: 'smooth' }); });
      paint();
    });
  }
  function decoded(im) { if (im && im.decode && !im.__decoded) { im.__decoded = true; try { im.decode().catch(function () {}); } catch (e) {} } }
  function bind() {
    bindStrips(); bindTileStrips();
    // the collection under Products (Chrome.tsx): open while a mouse rests on it or a keyboard is inside; never on a tap
    var shop = D.header && qs('[data-shop]', D.header);
    if (shop && !shop.__vero) {
      shop.__vero = true;
      shop.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') shop.setAttribute('data-open', ''); });
      shop.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') shop.removeAttribute('data-open'); });
      shop.addEventListener('focusin', function () { shop.setAttribute('data-open', ''); });
      shop.addEventListener('focusout', function (e) { if (!shop.contains(e.relatedTarget)) shop.removeAttribute('data-open'); });
      shop.addEventListener('keydown', function (e) { if (e.key === 'Escape') shop.removeAttribute('data-open'); });
    }
    var room = D.room;
    if (!room.__vero) {
      room.__vero = true;
      room.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { state.inRoom = true; paintShelf(); } });
      room.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { state.inRoom = false; setHovered(null); paintShelf(); } });
    }
    ITEMS.forEach(function (it) {
      var L = D.layers[it.handle];
      if (L.hit && !L.hit.__vero) {
        L.hit.__vero = true; L.hit.__href = L.hit.getAttribute('href');
        L.hit.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') setHovered(it.handle); });
        L.hit.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') setHovered(null); });
        L.hit.addEventListener('focus', function () { if (L.hit.matches(':focus-visible')) setHovered(it.handle); });
        L.hit.addEventListener('blur', function () { setHovered(null); });
      }
      if (L.index && !L.index.__vero && !it.guest) { // a guest's row lights nothing at rest (ProductIndex.tsx)
        L.index.__vero = true;
        L.index.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') setHovered(it.handle); });
        L.index.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') setHovered(null); });
        L.index.addEventListener('focus', function () { if (L.index.matches(':focus-visible')) setHovered(it.handle); });
        L.index.addEventListener('blur', function () { setHovered(null); });
      }
      if (L.marker && !L.marker.__vero) { L.marker.__vero = true; L.marker.addEventListener('animationend', function () { L.marker.classList.remove('marker-breathe'); }); }
      // both layers appear together, once both have loaded — and decoded (see decoded() below)
      var imgs = L.erasable.filter(function (el) { return el.tagName === 'IMG'; });
      var check = function () { if (imgs.every(function (im) { return im.complete && im.naturalWidth > 0; })) { imgs.forEach(decoded); L.erasable.forEach(function (el) { setAttr(el, 'data-loaded', true); }); } };
      imgs.forEach(function (im) { if (!im.__vero) { im.__vero = true; im.addEventListener('load', check); im.addEventListener('error', check); } });
      check();
    });
    // the entrance plays on the photograph itself, with a safety net if the load event never comes
    var plate = D.plate;
    // the blur-up goes with the photograph's load, as next/image drops its placeholder (Shelf.tsx onLoad → ready); the
    // photograph is decoded once, at full size, the moment it arrives — next/image awaits img.decode() on every image,
    // so on the site the push-in's re-raster at twice the scale finds the pixels already in the cache. Without it Chrome
    // decoded the 3840px plate again mid-move (70 ms on a worker, the main thread's commit waiting 40–80 ms): one visible
    // hitch in the camera spring on a 2x display (18 Sep 2026). Fire and forget; a failure changes nothing.
    var ready = function () { if (state.ready) return; state.ready = true; decoded(plate); setAttr(D.room, 'data-ready', true); paintShelf(); aimCamera(false); };
    if (plate && !plate.__vero) { plate.__vero = true; plate.addEventListener('load', ready); }
    if (plate && plate.complete && plate.naturalWidth > 0) ready(); else setTimeout(ready, 1500);
  }

  function render() { paintShelf(); syncView(); standing(); renderCart(); }
  function init() {
    if (!collect()) return false;
    // the theme editor re-renders the section with every sheet closed: a sheet that was open gives its lock back, or the
    // page would stay held still under a drawer that no longer exists (26 Sep 2026 review)
    if (cartState.open) { cartState.open = false; cartState.returnTo = null; cartState.trap = null; lock(false); }
    if (state.panel) { state.panel = null; panelReturn = null; lock(false); }
    [D.cart, D.cartDim, D.panel].forEach(function (el) { if (el) { el.removeAttribute('data-open'); el.setAttribute('hidden', ''); } });
    state.active = D.room.getAttribute('data-active') || null;
    if (state.active && !D.articleFor[state.active]) state.active = null; // a product with a view but no place in the photograph (LOCK) stays active
    var served = D.room.getAttribute('data-page') || null;
    state.page = served && D.pages[served] ? served : null; state.pageTitle = state.page ? document.title : '';
    state.anchor = null; INDEX = null;
    var c = new URLSearchParams(location.search).get('c');
    state.filter = (c === 'shower' || c === 'style') ? c : null;
    state.here = state.active; if (state.active) state.page = null; state.intent = null; state.hovered = null; state.panel = null; state.ready = false;
    setAttr(D.room, 'data-ready', false);
    // The server framed the right view; the camera starts a hair wide and
    // settles on the photograph once it has loaded — the room breathing in.
    stopCamera(); cam.vx = cam.vy = cam.vs = 0;
    aimCamera(true);
    bind();
    render();
    armReveals(currentView());
    trackPage(state.page);
    return true;
  }

  // Shopify re-renders the section in the theme editor and under hot reload
  // without running scripts: bind again to whatever is there now.
  document.addEventListener('shopify:section:load', function () { init(); });
  document.addEventListener('shopify:section:select', function () { if (D) render(); });
  var pending = 0;
  function resync() { if (!D) return; paintShelf(); writeCamera(); syncView(); standing(); }
  function schedule() { if (pending) return; pending = requestAnimationFrame(function () { pending = 0; if (!D || !D.room.isConnected) init(); else resync(); }); }
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      if (!D) return;
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'childList') { if (!D.room.isConnected || !D.frame || !D.frame.isConnected) { schedule(); return; } continue; }
        var t = r.target;
        if (t === D.camera && D.camera.style.transform === cam.written) continue;
        if (t === D.home || isPageView(t) || t === D.room || t === D.camera || t === D.header || (t.classList && (t.classList.contains('v-product') || t.classList.contains('erasable')))) { schedule(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'data-active', 'data-erased', 'style'] });
  }

  // Weighted scroll (Stage.tsx): the page has some mass.
  if (window.Lenis && !reduced()) {
    try {
      var lenis = new window.Lenis({ duration: 1.1, wheelMultiplier: 0.9 });
      var loop = function (t) { lenis.raf(t); requestAnimationFrame(loop); };
      requestAnimationFrame(loop);
    } catch (e) {}
  }

  if (init()) {
    try { history.replaceState({ vero: true, handle: state.active, page: state.page, filter: state.filter }, '', location.href); } catch (e) {}
    loadCart();
  }
})();
